import React from 'react';

import Icon, {
  faArrowRight,
  faBan,
  faBriefcaseClock,
  faBusinessTime,
  faCalculator,
  faCalendar,
  faCar,
  faCircleExclamation,
  faCog,
  faCommentDollar,
  faCommentSms,
  faCouch,
  faDollarSign,
  faEye,
  faEyeSlash,
  faHouse,
  faLock,
  faMagnifyingGlass,
  faMapPin,
  faMinus,
  faMugHot,
  faPersonWalkingArrowRight,
  faPlus,
  faRoute,
  faUserNurse,
  faUserPen,
  faUtensils,
  faXmark,
} from '~components/icon';
import IconStack from '~components/IconStack';

// Component for displaying a category of icons with a header
const IconCategory: React.FC<{ title: string; icons: { icon: React.ReactNode; title: string }[] }> = ({
  title,
  icons,
}) => (
  <>
    <div className="fw-bold dropdown-header legend-sub-title mt-2">{title}</div>
    <div className="dropdown-divider" />
    {icons.map(({ icon, title }) => (
      <div key={title} className="legend-item dropdown-item">
        <span className="title">{title}</span>
        <span className="icon">{icon}</span>
      </div>
    ))}
  </>
);

const Legend: React.FC = () => {
  // Transport/travel related icons
  const transportIcons = [
    {
      icon: (
        <IconStack
          id="travel_exempt"
          title="Travel Exempt"
          icons={[
            { icon: faCar, size: 'xs' },
            { icon: faBan, size: 'lg' },
          ]}
        />
      ),
      title: 'Travel Exempt',
    },
    {
      icon: <Icon icon={faCar} size="sm" />,
      title: 'Travel (Between Appointments)',
    },
    {
      icon: (
        <div className="dual-icon">
          <Icon icon={faHouse} size="sm" />
          <Icon icon={faArrowRight} size="sm" />
        </div>
      ),
      title: 'Travel From Home',
    },
    {
      icon: <Icon icon={faRoute} />,
      title: 'Total Distance',
    },
  ];

  // Break types
  const breakIcons = [
    {
      icon: <Icon icon={faUtensils} />,
      title: 'Meal Break',
    },
    {
      icon: <Icon icon={faMugHot} />,
      title: 'Tea Break',
    },
    {
      icon: <Icon icon={faCouch} />,
      title: 'Shift Break',
    },
  ];

  // Appointment types
  const appointmentTypeIcons = [
    {
      icon: <Icon icon={faCommentDollar} />,
      title: 'Active Cancellation',
    },
    {
      icon: <Icon icon={faPersonWalkingArrowRight} />,
      title: 'Leave Entry',
    },
    {
      icon: <Icon icon={faUserNurse} />,
      title: 'Predicted Shift',
    },
    {
      icon: <Icon icon={faBriefcaseClock} />,
      title: 'No Appointments',
    },
  ];

  // Payment related icons
  const paymentIcons = [
    {
      icon: <Icon icon={faLock} />,
      title: 'Fixed Pay',
    },
    {
      icon: (
        <IconStack
          id="is_shift_pay"
          title="Shift Pay"
          icons={[
            { icon: faCalendar, size: 'lg' },
            { icon: faDollarSign, size: 'xs', transform: 'down-4', style: { color: 'white' } },
          ]}
        />
      ),
      title: 'Shift Pay',
    },
  ];

  // Statistics and totals
  const statsIcons = [
    {
      icon: <Icon icon={faCalculator} />,
      title: 'All Totals',
    },
    {
      icon: <Icon icon={faBusinessTime} />,
      title: 'Appointment Totals',
    },
  ];

  // Interface controls
  const interfaceIcons = [
    {
      icon: <Icon icon={faEye} />,
      title: 'Show/Enable Feature',
    },
    {
      icon: <Icon icon={faEyeSlash} />,
      title: 'Hide/Disable Feature',
    },
    {
      icon: <Icon icon={faUserPen} />,
      title: 'Edit / Bulk assign workers',
    },
  ];
  //  Menus
  const menus = [
    {
      icon: <Icon icon={faMagnifyingGlass} />,
      title: 'Search',
    },
    {
      icon: <Icon icon={faCog} />,
      title: 'Settings',
    },
    {
      icon: <Icon icon={faCircleExclamation} />,
      title: 'Alerts',
    },
    {
      icon: <Icon icon={faMapPin} />,
      title: 'Legend',
    },
  ];
  // Actions
  const actionIcons = [
    {
      icon: <Icon icon={faPlus} />,
      title: 'Add/Select All',
    },
    {
      icon: <Icon icon={faMinus} />,
      title: 'Remove/Clear All',
    },
    {
      icon: <Icon icon={faCommentSms} />,
      title: 'SMS/Bidding',
    },
    {
      icon: <Icon icon={faXmark} />,
      title: 'Cancel/Close',
    },
  ];

  return (
    <div className="legend">
      <div className="fw-bold dropdown-header legend-title">Legend</div>
      <IconCategory title="Transport" icons={transportIcons} />
      <IconCategory title="Break Types" icons={breakIcons} />
      <IconCategory title="Appointment Types" icons={appointmentTypeIcons} />
      <IconCategory title="Payment" icons={paymentIcons} />
      <IconCategory title="Statistics" icons={statsIcons} />
      <IconCategory title="Interface Controls" icons={interfaceIcons} />
      <IconCategory title="Menus" icons={menus} />
      <IconCategory title="Actions" icons={actionIcons} />
    </div>
  );
};

export default Legend;
